import { AssetDeviceType } from '@casops/store/apis/registeredAssetsApi';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import {
  colDefDuration,
  columnDefAssetFaultCategory,
  columnDefAssetFaultLevel,
  columnDefAssetFaultName,
} from '@casops/components/tables/grid/columnDefs';
import { ModelName } from '@casops/store/api';
import {
  AssetFaultDisplayModel,
  RegisteredAssetDetailModel,
  useGetAssetQuery,
} from '@casops/store/apis/registeredAssetsApi';
import { ColDef } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAbsoluteTimePartialColDef } from '@casops/components/tables/cells/absolute-time/useAbsoluteTime';

type Model = AssetFaultDisplayModel;

const modelName = ModelName.AssetSelfTestActiveFaults;
const getRowId = (data: AssetFaultDisplayModel) => data.ts + '__' + data.flt;

export function AssetSelfTest() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, requestRemainder } = useOutletContext<{
    data: RegisteredAssetDetailModel | undefined;
    requestRemainder: Omit<ReturnType<typeof useGetAssetQuery>, 'data'>;
  }>();
  const isLoaded = requestRemainder.isSuccess;
  const hasFaultLevelData: boolean = isLoaded && !!data?.flts && data.flts.length > 0;
  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const absoluteTimeColDef = useAbsoluteTimePartialColDef<Model>();

  useEffect(() => {
    if (data && data.hw === AssetDeviceType.PPU) {
      // PPUs don't have configuration or diagnostics, so switch to the appropriate tab
      navigate(`/assets/asset/${data.id}/details`);
    }
  }, [data, navigate]);

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      { field: 'ts', headerName: t('Date'), sort: 'desc', ...absoluteTimeColDef },
      { ...columnDefAssetFaultCategory(t) },
      { ...columnDefAssetFaultName(t) },
      { ...colDefDuration(t('Duration')) },
      { ...columnDefAssetFaultLevel(t), maxWidth: 100 },
    ];

    return columnDefs;
  }, [t, absoluteTimeColDef]);

  const rowData = useMemo<AssetFaultDisplayModel[] | undefined>(() => {
    const now = DateTime.utc();
    return hasFaultLevelData
      ? data?.flts.map(flt => ({
          ...flt,
          dur: Math.floor(now.diff(DateTime.fromISO(flt.ts, { zone: 'utc' }), 'seconds').seconds),
        }))
      : [];
  }, [data?.flts, hasFaultLevelData]);

  const sliceKey = `${modelName}--${data?.id}`; // Create a unique sliceKey for each asset's self test table.

  return (
    <CollectionView<Model>
      sliceKey={sliceKey}
      modelName={modelName}
      columnDefs={columnDefs}
      rowData={rowData}
      hasData={hasFaultLevelData}
      getRowId={getRowId}
      noDataDescription={noResultsDescription}
    />
  );
}

export default AssetSelfTest;
